<template>
  <div class="bg-white px-4 py-4 custom-card" :class="[minHeight, { 'custom-card--static': props.hideButton }]" v-if="props.data" @click="goTo(props.data?.link)">
    <div class="d-flex justify-content-center align-items-center mb-3" :style="{ height: props.data?.imageBoxHeight || 'unset'}">
      <img :style="{ width: props.data?.imageWidth || '100%' }" :src="props.data.image" alt="" v-if="props.data?.image" />
    </div>
    <h3
      v-if="props.data.name"
      ref="titleEl"
      v-html="props.data.name"
      class="training-title lh-sm fw-bold mb-3"
    ></h3>
    <p v-html="props.data.description ?? ''" class="font-14"></p>

    <div class="d-grid gap-2 button-container" v-if="!props.hideButton">
      <a v-if="props.simpleLink" class="btn text-uppercase training-link" :class="props.btnClass" :href="props.data.link"
        :target="props.target" @click.stop>{{ props.linkText }}</a>
      <router-link class="btn text-uppercase training-link" :class="props.btnClass" :to="props.data.link"
        :target="props.target" @click="($event) => $event.preventDefault()" v-else>{{ props.linkText }}</router-link>
    </div>
  </div>
</template>

<script setup>
import { defineProps, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const props = defineProps({
  data: Object,
  linkText: {
    type: String,
  },
  target: {
    type: String,
    default: "_self",
  },
  simpleLink: {
    type: Boolean,
    default: false,
  },
  btnClass: {
    type: String,
    default: "btn-danger",
  },
  minHeight: {
    type: String,
    default: "simple-container",
  },
  hideButton: {
    type: Boolean,
    default: false,
  },
});

const titleEl = ref(null);
let resizeObserver = null;

const MAX_TITLE_FONT_SIZE = 28;
const MIN_TITLE_FONT_SIZE = 10;

const fitTitleToSingleLine = () => {
  const el = titleEl.value;
  if (!el) return;

  const titleText = (el.textContent || "").trim();
  const isSingleWord = titleText.length > 0 && !/\s/.test(titleText);

  if (!isSingleWord) {
    el.style.whiteSpace = "normal";
    el.style.overflow = "visible";
    el.style.fontSize = `${MAX_TITLE_FONT_SIZE}px`;
    return;
  }

  el.style.whiteSpace = "nowrap";
  el.style.overflow = "hidden";
  let size = MAX_TITLE_FONT_SIZE;
  el.style.fontSize = `${size}px`;

  while (size > MIN_TITLE_FONT_SIZE && el.scrollWidth > el.clientWidth) {
    size -= 1;
    el.style.fontSize = `${size}px`;
  }
};

onMounted(async () => {
  await nextTick();
  fitTitleToSingleLine();

  const target = titleEl.value?.parentElement;
  if (target && typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => {
      fitTitleToSingleLine();
    });
    resizeObserver.observe(target);
  }

  window.addEventListener("resize", fitTitleToSingleLine);
});

watch(
  () => props.data?.name,
  async () => {
    await nextTick();
    fitTitleToSingleLine();
  }
);

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
  window.removeEventListener("resize", fitTitleToSingleLine);
});

const goTo = (link) => {
  if (props.hideButton || !link) return;
  if (!props.simpleLink) {
    router.push(link);
  } else if (props.target == "_blank" && props.simpleLink) {
    window.open(link, "_blank");
  } else {
    location.href = link;
  }
};
</script>

<style scoped lang="scss">
@import "../../../styles/_variables.scss";

.custom-card {
  cursor: pointer;
  display: flex;
  flex-flow: column;

  &--static {
    cursor: default;
  }
}

.training-title {
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  word-break: normal;
  overflow-wrap: normal;
  hyphens: manual;
  font-size: 28px;
  line-height: 1.2;
  padding-top: 12px;
  color: #000000;
}

.training-link {

  &:hover,
  &:visited,
  &:active {
    color: white;
  }
}

.button-container {
  margin-top: auto;
}

.container-height {
  min-height: 530px;
}

.simple-container {
  min-height: 270px;
}
</style>
